/**
 * Microfone e câmera: botão que liga e desliga, mais a seta do menu de dispositivos. O estado vem do
 * `useTrackToggle` do LiveKit (a doc recomenda o hook em vez de reimplementar a lógica do botão).
 * Desligado vira vermelho com o ícone cortado (a cor nunca é o único sinal).
 */
import { useTrackToggle } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { Mic, MicOff, Video, VideoOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DeviceMenu } from './DeviceMenu';
import { FOCUS_RING, SIZE, TONE } from './controlStyles';

const KINDS = Object.freeze({
    mic: { source: Track.Source.Microphone, on: Mic, off: MicOff, noun: 'microfone', device: 'audioinput' },
    cam: { source: Track.Source.Camera, on: Video, off: VideoOff, noun: 'câmera', device: 'videoinput' },
});

/**
 * Parte visual, sem hooks.
 * @param {{
 *   kind: 'mic' | 'cam', enabled: boolean, variant: keyof typeof SIZE,
 *   buttonProps?: object, menu?: React.ReactNode,
 * }} props
 */
export function MediaControlView({ kind, enabled, variant, buttonProps = {}, menu }) {
    const { on: OnIcon, off: OffIcon, noun } = KINDS[kind];
    const Icon = enabled ? OnIcon : OffIcon;
    const label = `${enabled ? 'Desligar' : 'Ligar'} ${noun}`;
    return (
        <div className={cn('inline-flex overflow-hidden rounded-full', enabled ? TONE.on : TONE.off)}>
            <button
                type="button"
                {...buttonProps}
                aria-label={label}
                title={label}
                className={cn('flex items-center justify-center hover:bg-white/10 disabled:opacity-60', SIZE[variant].btn, FOCUS_RING)}
            >
                <Icon className={SIZE[variant].icon} aria-hidden="true" />
            </button>
            {menu}
        </div>
    );
}

/** @param {{ kind: 'mic' | 'cam', variant: keyof typeof SIZE }} props */
export function MediaControl({ kind, variant }) {
    const { source, device } = KINDS[kind];
    const { buttonProps, enabled } = useTrackToggle({ source });
    return (
        <MediaControlView
            kind={kind}
            enabled={enabled}
            variant={variant}
            buttonProps={buttonProps}
            menu={<DeviceMenu kind={device} variant={variant} />}
        />
    );
}
